import { isEqual, pick, compact, flattenDeep, escape } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';

import Wix from '../../services/wix-sdk-polyfill';
import { setPageMetadata } from '../../actions/set-page-metadata';
import { getImageUrl } from '../../services/image-utils';
import { isInSite } from '../../services/view-mode';

const MAX_DESCRIPTION_LENGTH = 160;

const getMetadata = ({ title, description, image, type, noIndex }) => {
  const descriptionEscaped = escape(description);
  const titleEscaped = escape(title);

  return flattenDeep(
    compact([
      { property: 'og:type', content: type },
      description && [
        { name: 'description', content: descriptionEscaped },
        { property: 'og:description', content: descriptionEscaped.slice(0, MAX_DESCRIPTION_LENGTH) },
      ],
      title && [{ property: 'og:title', content: titleEscaped }],
      image && [
        { property: 'og:image', content: getImageUrl(image) },
        { property: 'og:image:width', content: image.width },
        { property: 'og:image:height', content: image.height },
      ],
      noIndex && [{ name: 'robots', content: 'noindex' }],
    ]),
  );
};

const METADATA_PROPS = ['title', 'description', 'image', 'type', 'noIndex'];

export class PageMetadata extends Component {
  // image object is cloned on get so it's not shallow equal
  shouldComponentUpdate(nextProps) {
    return !isEqual(pick(this.props, METADATA_PROPS), pick(nextProps, METADATA_PROPS));
  }

  render() {
    const { title, description, canonicalUrl, ampUrl, prevUrl, nextUrl, setPageMetadata } = this.props;

    const meta = getMetadata(pick(this.props, METADATA_PROPS));
    const links = compact([
      canonicalUrl && { rel: 'canonical', href: canonicalUrl },
      ampUrl && { rel: 'amphtml', href: ampUrl },
      prevUrl && { rel: 'prev', href: prevUrl },
      nextUrl && { rel: 'next', href: nextUrl },
    ]);

    return (
      <Helmet
        title={title}
        meta={meta}
        link={links}
        onChangeClientState={() => {
          if (isInSite()) {
            setPageMetadata({ title, description });
            Wix.Metadata.removeAppMetadata({ canonical: true, prev: true, next: true });
            links.forEach(({ rel, href }) => {
              Wix.Metadata.setAppMetadata({ [rel]: { href } });
            });
          }
        }}
        encodeSpecialCharacters={false}
      />
    );
  }
}

PageMetadata.propTypes = {
  canonicalUrl: PropTypes.string,
  ampUrl: PropTypes.string,
  prevUrl: PropTypes.string,
  nextUrl: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.object,
  noIndex: PropTypes.bool,
  setPageMetadata: PropTypes.func.isRequired,
};

PageMetadata.defaultProps = {
  title: '',
  type: 'website',
};

const mapDispatchToProps = {
  setPageMetadata,
};

export default connect(
  undefined,
  mapDispatchToProps,
)(PageMetadata);
